<!--
  Backend (kepttam-admin-backend, api/v1 + auth):
  - GET campaign/:campaign_id/members/count?period=&q= → total
  - GET campaign/:campaign_id/members/summary/points?period=&q= → total_points
  - GET campaign/:campaign_id/members/summary/redemptions?period=&q= → total_redemptions
  - GET campaign/:campaign_id/members/pagination?page=&perPage=&period=&q=
-->
<template>
  <div>
    <b-row class="mb-4">
      <b-col cols="12" md="4" class="mb-3 mb-md-0">
        <b-card class="shadow-sm h-100" body-class="py-4">
          <div class="text-muted small mb-1">
            {{ $t("PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.TOTAL_LABEL") }}
          </div>
          <div v-if="countLoading" class="h4 mb-0">
            {{
              $t("PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.LOADING_COUNT")
            }}
          </div>
          <div v-else-if="countError" class="text-warning small mb-0">
            {{
              $t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.COUNT_UNAVAILABLE"
              )
            }}
          </div>
          <div v-else class="h3 mb-0 font-weight-bold text-primary">
            {{ formatStatNumber(memberTotal) }}
          </div>
        </b-card>
      </b-col>
      <b-col cols="12" md="4" class="mb-3 mb-md-0">
        <b-card class="shadow-sm h-100" body-class="py-4">
          <div class="text-muted small mb-1">
            {{
              $t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.TOTAL_POINTS_LABEL"
              )
            }}
          </div>
          <div v-if="pointsLoading" class="h4 mb-0">
            {{
              $t("PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.LOADING_COUNT")
            }}
          </div>
          <div v-else-if="pointsError" class="text-warning small mb-0">
            {{
              $t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.POINTS_UNAVAILABLE"
              )
            }}
          </div>
          <div v-else class="h3 mb-0 font-weight-bold text-primary">
            {{ formatStatNumber(memberTotalPoints) }}
          </div>
        </b-card>
      </b-col>
      <b-col cols="12" md="4">
        <b-card class="shadow-sm h-100" body-class="py-4">
          <div class="text-muted small mb-1">
            {{
              $t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.TOTAL_REDEMPTIONS_LABEL"
              )
            }}
          </div>
          <div v-if="redemptionsLoading" class="h4 mb-0">
            {{
              $t("PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.LOADING_COUNT")
            }}
          </div>
          <div v-else-if="redemptionsError" class="text-warning small mb-0">
            {{
              $t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.REDEMPTIONS_UNAVAILABLE"
              )
            }}
          </div>
          <div v-else class="h3 mb-0 font-weight-bold text-primary">
            {{ formatStatNumber(memberTotalRedemptions) }}
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="mb-3" align-v="end">
      <b-col cols="12" md="5" class="mb-2 mb-md-0">
        <b-form-group class="mb-0">
          <b-input-group size="md">
            <b-form-input
              v-model="pagination.filter"
              :placeholder="$t('PAGE.DEFAULT.TABLE.SEARCH')"
              type="search"
              debounce="500"
              @update="onSearchChange"
            />
            <b-input-group-append>
              <b-button
                :disabled="!pagination.filter"
                @click="
                  pagination.filter = '';
                  resetPageAndFetch();
                "
              >
                {{ $t("PAGE.DEFAULT.TABLE.CLEAR") }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="7">
        <b-form-group
          class="mb-0"
          :label="
            $t('PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.JOINED_FILTER')
          "
          label-cols="auto"
          label-align="right"
        >
          <b-form-radio-group
            v-model="joinedFilter"
            :options="joinedOptions"
            buttons
            button-variant="outline-primary"
            size="sm"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <div class="table-content">
      <div class="table-responsive">
        <b-table
          :striped="false"
          :small="true"
          :items="items"
          :fields="fields"
          :sort-by.sync="pagination.orderBy"
          :sort-desc.sync="pagination.isDesc"
          :no-sort-reset="true"
          :no-local-sorting="true"
          :busy.sync="isBusy"
          head-variant="light"
          @sort-changed="onSortChanged"
        >
          <template #cell(name)="data">
            {{ formatMemberName(data.item) }}
          </template>
          <template #cell(phone_number)="data">
            <span v-if="data.item.phone_number">{{
              data.item.phone_number
            }}</span>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(customer_created_at)="data">
            <span v-if="data.item.customer_created_at">{{
              formatDateTime(data.item.customer_created_at)
            }}</span>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              @click="goMemberDetail(data.item.customer_campaign_id)"
            >
              {{
                $t(
                  "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.ACTION_VIEW_DETAIL"
                )
              }}
            </b-button>
          </template>
        </b-table>
        <p
          v-if="!isBusy && items.length < 1"
          class="text-center pt-3 text-muted"
        >
          {{
            listError
              ? $t(
                  "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.LIST_UNAVAILABLE"
                )
              : $t("PAGE.DEFAULT.TABLE.DATA_NOT_FOUND")
          }}
        </p>
      </div>

      <b-row class="justify-content-between mt-2">
        <b-col cols="12" md="4">
          <b-form-group
            :label="$t('PAGE.DEFAULT.TABLE.PER_PAGE')"
            label-cols="5"
            label-size="sm"
            class="mb-0"
          >
            <b-form-select
              v-model="pagination.perPage"
              size="sm"
              :options="paginationOptions"
              @change="fetchList"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="8">
          <b-pagination
            v-model="pagination.page"
            :total-rows="total"
            :per-page="pagination.perPage"
            align="right"
            class="mb-0"
            @input="fetchList"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";

export default {
  name: "CampaignMembersTab",
  props: {
    campaign_id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      memberTotal: null,
      memberTotalPoints: null,
      memberTotalRedemptions: null,
      countLoading: false,
      countError: false,
      pointsLoading: false,
      pointsError: false,
      redemptionsLoading: false,
      redemptionsError: false,
      listError: false,
      isBusy: false,
      items: [],
      total: 0,
      joinedFilter: "all",
      pagination: {
        page: 1,
        perPage: 25,
        orderBy: "created_at",
        isDesc: true,
        filter: ""
      },
      paginationOptions: [25, 50, 100],
      fields: [
        {
          key: "name",
          label: this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.COL_NAME"
          ),
          sortable: false
        },
        {
          key: "phone_number",
          label: this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.COL_PHONE"
          ),
          sortable: false
        },
        {
          key: "customer_created_at",
          label: this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.COL_CUSTOMER_REGISTERED"
          ),
          sortable: false
        },
        {
          key: "actions",
          label: this.$t("PAGE.DEFAULT.TABLE.FIELDS.ACTION.TITLE"),
          sortable: false,
          tdClass: "text-nowrap"
        }
      ]
    };
  },
  computed: {
    joinedOptions() {
      return [
        {
          value: "all",
          text: this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.FILTER_ALL"
          )
        },
        {
          value: "today",
          text: this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.FILTER_NEW_TODAY"
          )
        },
        {
          value: "month",
          text: this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.FILTER_NEW_MONTH"
          )
        }
      ];
    }
  },
  mounted() {
    this.refreshMemberSummaryStats();
    this.fetchList();
  },
  watch: {
    /**
     * Do not use @change on b-form-radio-group: it can fire before v-model updates,
     * so API calls would use the previous period. Watch runs after joinedFilter is current.
     */
    joinedFilter() {
      this.pagination.page = 1;
      this.$nextTick(() => {
        this.refreshMemberSummaryStats();
        this.fetchList();
      });
    }
  },
  methods: {
    formatMemberName(row) {
      const fn = row.first_name || "";
      const ln = row.last_name || "";
      const combined = `${fn} ${ln}`.trim();
      if (combined) {
        return combined;
      }
      if (row.display_name) {
        return row.display_name;
      }
      return "—";
    },
    formatDateTime(v) {
      if (!v) {
        return "—";
      }
      try {
        return new Date(v).toLocaleString("th-TH");
      } catch (e) {
        return v;
      }
    },
    onSearchChange() {
      this.pagination.page = 1;
      this.refreshMemberSummaryStats();
      this.fetchList();
    },
    resetPageAndFetch() {
      this.pagination.page = 1;
      this.refreshMemberSummaryStats();
      this.fetchList();
    },
    formatStatNumber(v) {
      if (v == null || Number.isNaN(Number(v))) {
        return "—";
      }
      return Number(v).toLocaleString("th-TH");
    },
    onSortChanged(event) {
      this.pagination.isDesc = event.sortDesc;
      this.pagination.orderBy = event.sortBy || "created_at";
      this.fetchList();
    },
    summaryStatsParams() {
      return {
        period: this.joinedFilter,
        q: (this.pagination.filter || "").trim()
      };
    },
    async refreshMemberSummaryStats() {
      await Promise.all([
        this.fetchMemberCount(),
        this.fetchMemberPointsTotal(),
        this.fetchMemberRedemptionsCount()
      ]);
    },
    async fetchMemberCount() {
      this.countLoading = true;
      this.countError = false;
      try {
        ApiService.setHeader();
        const { data } = await ApiService.getParams(
          `campaign/${this.campaign_id}/members/count`,
          {
            params: this.summaryStatsParams()
          }
        );
        if (data.status === "success" && data.data != null) {
          const t = data.data.total;
          this.memberTotal = t != null && t !== "" ? Number(t) : 0;
        } else {
          this.countError = true;
          this.memberTotal = null;
        }
      } catch (e) {
        this.countError = true;
        this.memberTotal = null;
      } finally {
        this.countLoading = false;
      }
    },
    async fetchMemberPointsTotal() {
      this.pointsLoading = true;
      this.pointsError = false;
      try {
        ApiService.setHeader();
        const { data } = await ApiService.getParams(
          `campaign/${this.campaign_id}/members/summary/points`,
          {
            params: this.summaryStatsParams()
          }
        );
        if (data.status === "success" && data.data != null) {
          const tp = data.data.total_points;
          this.memberTotalPoints = tp != null && tp !== "" ? Number(tp) : 0;
        } else {
          this.pointsError = true;
          this.memberTotalPoints = null;
        }
      } catch (e) {
        this.pointsError = true;
        this.memberTotalPoints = null;
      } finally {
        this.pointsLoading = false;
      }
    },
    async fetchMemberRedemptionsCount() {
      this.redemptionsLoading = true;
      this.redemptionsError = false;
      try {
        ApiService.setHeader();
        const { data } = await ApiService.getParams(
          `campaign/${this.campaign_id}/members/summary/redemptions`,
          {
            params: this.summaryStatsParams()
          }
        );
        if (data.status === "success" && data.data != null) {
          const tr = data.data.total_redemptions;
          this.memberTotalRedemptions =
            tr != null && tr !== "" ? Number(tr) : 0;
        } else {
          this.redemptionsError = true;
          this.memberTotalRedemptions = null;
        }
      } catch (e) {
        this.redemptionsError = true;
        this.memberTotalRedemptions = null;
      } finally {
        this.redemptionsLoading = false;
      }
    },
    async fetchList() {
      this.isBusy = true;
      this.listError = false;
      try {
        ApiService.setHeader();
        const { data } = await ApiService.getParams(
          `campaign/${this.campaign_id}/members/pagination`,
          {
            params: {
              page: String(this.pagination.page),
              perPage: String(this.pagination.perPage),
              period: this.joinedFilter,
              q: (this.pagination.filter || "").trim()
            }
          }
        );
        if (data.status === "success" && data.data) {
          const rows = data.data.data || [];
          this.items = rows;
          this.total = data.data.total != null ? Number(data.data.total) : 0;
        } else {
          this.items = [];
          this.total = 0;
          this.listError = true;
        }
      } catch (e) {
        this.items = [];
        this.total = 0;
        this.listError = true;
      } finally {
        this.isBusy = false;
      }
    },
    openMemberRoute(routeName, customerCampaignId, query = {}) {
      const r = this.$router.resolve({
        name: routeName,
        params: {
          campaignId: String(this.campaign_id),
          customerCampaignId: String(customerCampaignId)
        },
        query
      });
      window.open(r.href, "_blank", "noopener,noreferrer");
    },
    goMemberDetail(customerCampaignId) {
      this.openMemberRoute("campaignMemberDetail", customerCampaignId);
    }
  }
};
</script>
